import { credentialsFor } from "../../common/utils";

// Probes any audio/video Blob or File and resolves to its length in whole
// seconds. Returns null when:
//  - the input is missing, or not an audio/video MIME type
//  - the browser fails to load metadata (corrupt/unsupported codec)
//  - the duration is non-finite (e.g. WebM with unknown length)
//
// Used to attach `durationSeconds` to media upload requests so the backend
// doesn't need to download and probe the file itself.
export function getMediaDuration(file) {
  return new Promise((resolve) => {
    if (!file || typeof window === "undefined") {
      resolve(null);
      return;
    }
    const type = (file.type || "").toLowerCase();
    const isVideo = type.startsWith("video/");
    const isAudio = type.startsWith("audio/");
    if (!isVideo && !isAudio) {
      resolve(null);
      return;
    }
    let url;
    try {
      url = URL.createObjectURL(file);
    } catch {
      resolve(null);
      return;
    }
    const el = document.createElement(isVideo ? "video" : "audio");
    el.preload = "metadata";
    const cleanup = () => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* ignore */
      }
    };
    const finish = (value) => {
      cleanup();
      resolve(value && !isNaN(value) && isFinite(value) ? Math.round(value) : null);
    };
    el.onloadedmetadata = () => finish(el.duration);
    el.onerror = () => finish(null);
    el.src = url;
  });
}

// Same as getMediaDuration but for a media URL (e.g. an already-uploaded cloud
// video). Loads only metadata into a detached <video> element and resolves to
// whole seconds, or null (missing url / load failure / non-finite / timeout).
// Used to enforce the transcription duration limit before sending the job.
export function getMediaDurationFromUrl(url, { timeoutMs = 12000 } = {}) {
  return new Promise((resolve) => {
    if (!url || typeof window === "undefined") {
      resolve(null);
      return;
    }
    const el = document.createElement("video");
    el.preload = "metadata";
    el.muted = true;
    let done = false;
    let timer = null;
    const finish = (value) => {
      if (done) return;
      done = true;
      if (timer) clearTimeout(timer);
      try {
        el.src = "";
        el.removeAttribute("src");
        el.load();
      } catch {
        /* ignore */
      }
      resolve(value && !isNaN(value) && isFinite(value) ? Math.round(value) : null);
    };
    el.onloadedmetadata = () => finish(el.duration);
    el.onerror = () => finish(null);
    timer = setTimeout(() => finish(null), timeoutMs);
    el.src = url;
  });
}

// Best-effort HEAD probe for a URL's byte size (Content-Length). Returns the
// size in bytes, or null when the request fails / is CORS-blocked / lacks the
// header. Cloud storage often blocks cross-origin HEAD, so treat null as
// "unknown" — the backend enforces the hard size limit regardless.
export async function getUrlContentLength(url) {
  if (!url || typeof fetch !== "function") return null;
  try {
    const res = await fetch(url, { method: "HEAD", ...credentialsFor(url) });
    if (!res.ok) return null;
    const len = parseInt(
      res.headers.get("Content-Length") || res.headers.get("content-length") || "",
      10
    );
    return Number.isFinite(len) && len > 0 ? len : null;
  } catch {
    return null;
  }
}
